import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { DashboardLayout } from '../../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../../components/ui/dialog';
import axios from 'axios';
import { toast } from 'sonner';
import { ArrowLeft, Package, Plus, Minus, ShoppingCart, History, Settings, Search, Trash2, ChevronRight, Edit2, Check, X, GripVertical } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { TagesgeschaeftShareBar } from '../../components/TagesgeschaeftShareBar';
import {
  DndContext, closestCenter, PointerSensor, TouchSensor, KeyboardSensor,
  useSensor, useSensors,
} from '@dnd-kit/core';
import {
  SortableContext, arrayMove, useSortable, verticalListSortingStrategy, sortableKeyboardCoordinates,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

const API = process.env.REACT_APP_BACKEND_URL;
const UNITS = ['Stück', 'Karton', 'kg', 'Liter', 'Packung', 'Palette'];

// ── Sortable Desktop Row (dnd-kit) ───────────────────────────────────────────
function SortableDesktopRow({ product, isAdmin, qty, vpeQty, vpeCount, itemTotal, category,
  lastOrderQty, cartVpe, editingExistingProduct, editExistingForm, setEditExistingForm,
  savingExistingEdit, saveExistingEdit, setEditingExistingProduct,
  removingProduct, updateQuantity, setQuantity, setVpeQuantity,
  openEditExisting, removeProductFromCategory }) {
  const { attributes, listeners, setNodeRef, setActivatorNodeRef, transform, transition, isDragging } = useSortable({ id: product.id });
  const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.4 : 1 };
  return (
    <React.Fragment>
      <tr ref={setNodeRef} style={style} className="border-b transition-colors hover:bg-gray-50 dark:bg-gray-800/50">
        {isAdmin && (
          <td className="px-2 py-3">
            <button ref={setActivatorNodeRef} {...listeners} {...attributes}
              className="cursor-grab active:cursor-grabbing touch-none text-gray-300 hover:text-gray-500 flex items-center justify-center w-6 h-6"
              aria-label="Verschieben">
              <GripVertical className="h-4 w-4" />
            </button>
          </td>
        )}
        <td className="px-4 py-3">
          <div className="font-medium text-sm">{product.name}</div>
          {product.packaging_unit && <div className="text-xs text-gray-500">{product.packaging_unit}</div>}
          {product.min_stock && <div className="text-xs text-amber-600 font-medium mt-0.5">Min: {product.min_stock} {product.unit || 'Stk'}</div>}
          <div className="text-xs mt-0.5 flex items-center gap-1">
            <span className="text-gray-400">↩</span>
            {lastOrderQty[product.id] !== undefined
              ? <span className="text-blue-600 dark:text-blue-400 font-medium">Letztes Mal: {lastOrderQty[product.id]} {product.unit || 'Stk'}</span>
              : <span className="text-gray-400">Letzte Bestellung: –</span>}
          </div>
        </td>
        <td className="px-4 py-3 text-sm font-mono text-gray-600 dark:text-gray-400">
          {product.internal_article_number || product.supplier_article_number || '-'}
        </td>
        <td className="px-4 py-3 text-sm text-right">
          {product.price_net ? `${product.price_net.toFixed(2)} €` : '-'}
        </td>
        <td className="px-4 py-3">
          <div className="flex items-center justify-center gap-2">
            <Button variant="outline" size="sm" onClick={() => updateQuantity(product.id, -1)} disabled={qty === 0}><Minus className="h-3 w-3" /></Button>
            <Input type="number" min="0" value={qty} onChange={e => setQuantity(product.id, e.target.value)} className="w-16 text-center" />
            <Button variant="outline" size="sm" onClick={() => updateQuantity(product.id, 1)} title={`+${vpeCount} Stk`}>
              <Plus className="h-3 w-3" />
              {vpeCount > 1 && <span className="text-[10px] ml-0.5 text-muted-foreground">{vpeCount}</span>}
            </Button>
          </div>
          {qty > 0 && vpeCount > 1 && (
            <p className="text-[10px] text-center text-muted-foreground mt-0.5">
              {qty % vpeCount === 0 ? `= ${qty / vpeCount} Karton${qty / vpeCount > 1 ? 's' : ''}` : `${Math.floor(qty / vpeCount)} Ktn + ${qty % vpeCount} Stk`}
            </p>
          )}
        </td>
        {category?.supplier_email && (
          <td className="px-4 py-3">
            <Input type="number" min="0" value={cartVpe[product.id] || ''} onChange={e => setVpeQuantity(product.id, e.target.value)} placeholder="0" className="w-16 text-center border-blue-300 focus:border-blue-500" />
          </td>
        )}
        <td className="px-4 py-3 text-sm text-right font-semibold">
          {itemTotal > 0 ? (
            <div>
              <span>{itemTotal.toFixed(2)} €</span>
              {vpeQty > 0 && qty > 0 && <div className="text-xs text-muted-foreground font-normal">{qty}×{product.price_net?.toFixed(2)}€ + {vpeQty}VPE×{vpeCount}×{product.price_net?.toFixed(2)}€</div>}
              {vpeQty > 0 && qty === 0 && <div className="text-xs text-muted-foreground font-normal">{vpeQty}×{vpeCount} Stk à {product.price_net?.toFixed(2)}€</div>}
            </div>
          ) : '-'}
        </td>
        {isAdmin && (
          <td className="px-2 py-3">
            <div className="flex items-center gap-1">
              <button onClick={() => openEditExisting(product)} title="Bearbeiten" className="p-1 text-gray-400 hover:text-blue-500 transition-colors"><Edit2 className="h-3.5 w-3.5" /></button>
              <button onClick={() => removeProductFromCategory(product)} disabled={removingProduct === product.id} className="p-1 text-gray-400 hover:text-red-500 transition-colors"><Trash2 className="h-3.5 w-3.5" /></button>
            </div>
          </td>
        )}
      </tr>
      {isAdmin && editingExistingProduct?.id === product.id && (
        <tr className="bg-blue-50 dark:bg-blue-950/20 border-b border-blue-200">
          <td colSpan={category?.supplier_email ? 7 : 6} className="px-4 py-3">
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2 items-end">
              {[['Produktname','name',2],['JET Art.-Nr.','internal_article_number',1],['Lief. Art.-Nr.','supplier_article_number',1],['VPE / Einheit','packaging_unit',1]].map(([lbl, fld, span]) => (
                <div key={fld} className={`space-y-1 col-span-${span}`}>
                  <label className="text-xs font-semibold text-blue-700">{lbl}</label>
                  <Input value={editExistingForm[fld] || ''} onChange={e => setEditExistingForm(f => ({...f, [fld]: e.target.value}))} className="h-8 text-sm" />
                </div>
              ))}
              <div className="space-y-1">
                <label className="text-xs font-semibold text-blue-700">Preis (Netto €)</label>
                <Input type="number" step="0.01" value={editExistingForm.price_net || ''} onChange={e => setEditExistingForm(f => ({...f, price_net: e.target.value}))} className="h-8 text-sm" />
              </div>
              <div className="space-y-1">
                <label className="text-xs font-semibold text-blue-700">MwSt %</label>
                <select value={editExistingForm.vat_rate ?? 19} onChange={e => setEditExistingForm(f => ({...f, vat_rate: parseInt(e.target.value)}))} className="h-8 w-full rounded-md border border-input bg-background px-2 text-sm">
                  <option value={19}>19 %</option><option value={7}>7 %</option><option value={0}>0 %</option>
                </select>
              </div>
              <div className="space-y-1">
                <label className="text-xs font-semibold text-blue-700">Warengruppe</label>
                <Input value={editExistingForm.product_group || ''} onChange={e => setEditExistingForm(f => ({...f, product_group: e.target.value}))} className="h-8 text-sm" placeholder="optional" />
              </div>
              <div className="col-span-2 sm:col-span-3 lg:col-span-6 flex gap-2 pt-1">
                <Button size="sm" onClick={saveExistingEdit} disabled={savingExistingEdit} className="gap-1.5 bg-blue-600 hover:bg-blue-700">
                  {savingExistingEdit ? <span className="animate-spin">⏳</span> : <Check className="h-3.5 w-3.5" />} Speichern
                </Button>
                <Button size="sm" variant="ghost" onClick={() => setEditingExistingProduct(null)} className="gap-1.5"><X className="h-3.5 w-3.5" /> Abbrechen</Button>
              </div>
            </div>
          </td>
        </tr>
      )}
    </React.Fragment>
  );
}

// ── Sortable Mobile Card (dnd-kit Touch Drag) ────────────────────────────────
function SortableMobileCard({ product, isAdmin, cart, cartVpe, lastOrderQty,
  editingExistingProduct, editExistingForm, setEditExistingForm,
  savingExistingEdit, saveExistingEdit, setEditingExistingProduct,
  updateQuantity, setQuantity, parseVpe, openEditExisting, removeProductFromCategory }) {
  const { attributes, listeners, setNodeRef, setActivatorNodeRef, transform, transition, isDragging } = useSortable({ id: product.id });
  const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.4 : 1, zIndex: isDragging ? 50 : 0 };
  const qty = cart[product.id] || 0;
  const vpe = parseVpe(product);
  return (
    <div ref={setNodeRef} style={style}>
      <Card className={isDragging ? 'shadow-2xl ring-2 ring-primary/40' : ''}>
        <CardContent className="pt-3 pb-3">
          <div className="space-y-2">
            <div className="flex items-start justify-between">
              <div className="flex items-start gap-2 flex-1">
                {isAdmin && (
                  <button ref={setActivatorNodeRef} {...listeners} {...attributes}
                    className="cursor-grab active:cursor-grabbing touch-none mt-1 p-1 text-gray-300 hover:text-gray-500 shrink-0"
                    aria-label="Verschieben">
                    <GripVertical className="h-5 w-5" />
                  </button>
                )}
                <div className="flex-1">
                  <h3 className="font-semibold text-sm">{product.name}</h3>
                  {product.internal_article_number && <p className="text-xs text-gray-500 font-mono">{product.internal_article_number}</p>}
                  {product.min_stock && <p className="text-xs text-amber-600 font-medium">Min: {product.min_stock} {product.unit || 'Stk'}</p>}
                  {product.price_net && <p className="text-xs text-blue-600 font-semibold">{product.price_net.toFixed(2)} € / {product.unit}</p>}
                  <p className="text-xs mt-0.5 flex items-center gap-1">
                    <span className="text-gray-400">↩</span>
                    {lastOrderQty[product.id] !== undefined
                      ? <span className="text-blue-600 dark:text-blue-400 font-medium">Letztes Mal: {lastOrderQty[product.id]} {product.unit || 'Stk'}</span>
                      : <span className="text-gray-400">Letzte Bestellung: –</span>}
                  </p>
                </div>
              </div>
              {isAdmin && (
                <div className="flex gap-1">
                  <button onClick={() => openEditExisting(product)} className="p-1 text-gray-300 hover:text-blue-500"><Edit2 className="h-4 w-4" /></button>
                  <button onClick={() => removeProductFromCategory(product)} className="p-1 text-gray-300 hover:text-red-500"><Trash2 className="h-4 w-4" /></button>
                </div>
              )}
            </div>
            {isAdmin && editingExistingProduct?.id === product.id && (
              <div className="mt-3 pt-3 border-t border-blue-200 space-y-2">
                <p className="text-xs font-semibold text-blue-700">Produkt bearbeiten:</p>
                <Input value={editExistingForm.name || ''} onChange={e => setEditExistingForm(f => ({...f, name: e.target.value}))} placeholder="Name" className="h-8 text-sm" />
                <div className="grid grid-cols-2 gap-2">
                  <Input value={editExistingForm.internal_article_number || ''} onChange={e => setEditExistingForm(f => ({...f, internal_article_number: e.target.value}))} placeholder="JET Art.-Nr." className="h-8 text-sm font-mono" />
                  <Input value={editExistingForm.supplier_article_number || ''} onChange={e => setEditExistingForm(f => ({...f, supplier_article_number: e.target.value}))} placeholder="Lief. Art.-Nr." className="h-8 text-sm font-mono" />
                  <Input value={editExistingForm.packaging_unit || ''} onChange={e => setEditExistingForm(f => ({...f, packaging_unit: e.target.value}))} placeholder="VPE z.B. 12x1l" className="h-8 text-sm" />
                  <Input type="number" step="0.01" value={editExistingForm.price_net || ''} onChange={e => setEditExistingForm(f => ({...f, price_net: e.target.value}))} placeholder="Preis €" className="h-8 text-sm" />
                </div>
                <div className="flex gap-2">
                  <Button size="sm" onClick={saveExistingEdit} disabled={savingExistingEdit} className="flex-1 gap-1 bg-blue-600 hover:bg-blue-700"><Check className="h-3.5 w-3.5" /> Speichern</Button>
                  <Button size="sm" variant="ghost" onClick={() => setEditingExistingProduct(null)} className="flex-1 gap-1"><X className="h-3.5 w-3.5" /> Abbrechen</Button>
                </div>
              </div>
            )}
            <div className="flex items-center justify-between pt-2 border-t">
              <div>
                <Label className="text-sm font-medium">Menge (Stk):</Label>
                {qty > 0 && vpe > 1 && (
                  <p className="text-[10px] text-muted-foreground">
                    {qty % vpe === 0 ? `= ${qty / vpe} Karton(s)` : `${Math.floor(qty / vpe)} Ktn + ${qty % vpe} Stk`}
                  </p>
                )}
              </div>
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" onClick={() => updateQuantity(product.id, -1)} disabled={qty === 0} className="h-9 w-9 p-0"><Minus className="h-4 w-4" /></Button>
                <Input type="number" min="0" value={qty} onChange={e => setQuantity(product.id, e.target.value)} className="w-16 text-center h-9" />
                <Button variant="outline" size="sm" onClick={() => updateQuantity(product.id, 1)} className="h-9 w-10 p-0">
                  <Plus className="h-4 w-4" />
                  {vpe > 1 && <span className="text-[9px] ml-0.5">{vpe}</span>}
                </Button>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}



export const BestellKategorie = () => {
  const { categoryId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin' || user?.role === 'superadmin' || user?.role === 'stationsleitung';
  
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [category, setCategory] = useState(null);
  const [products, setProducts] = useState([]);
  const [cart, setCart] = useState({}); // { productId: quantity }
  const [cartVpe, setCartVpe] = useState({}); // { productId: quantity_vpe }
  const [lastOrderQty, setLastOrderQty] = useState({}); // { productId: quantity } – nur zur Anzeige, nicht editierbar
  const [showNotesDialog, setShowNotesDialog] = useState(false);
  const [notes, setNotes] = useState('');

  // Produkt-Management (nur Admin)
  const [showProductModal, setShowProductModal] = useState(false);
  const [productTab, setProductTab] = useState('existing'); // 'existing' | 'new'
  const [allProducts, setAllProducts] = useState([]); // Alle Produkte des Kunden
  const [productSearch, setProductSearch] = useState('');
  const [addingProduct, setAddingProduct] = useState(null); // ID des Produkts gerade hinzugefügt
  const [newProductForm, setNewProductForm] = useState({
    name: '', internal_article_number: '', supplier_article_number: '',
    unit: 'Stück', price_net: '', packaging_unit: '', ean: '', min_stock: '', product_group: '', vat_rate: 19
  });
  const [savingNewProduct, setSavingNewProduct] = useState(false);
  const [removingProduct, setRemovingProduct] = useState(null);
  // Bearbeitung vorhandener Produkte
  const [editingExistingProduct, setEditingExistingProduct] = useState(null);
  const [editExistingForm, setEditExistingForm] = useState({});
  const [savingExistingEdit, setSavingExistingEdit] = useState(false);

  useEffect(() => {
    fetchData();
    loadLastOrderReference(); // Letzte Bestellung als Referenz laden (silent)
  }, [categoryId]);

  const fetchData = async () => {
    try {
      const headers = { 'Authorization': `Bearer ${localStorage.getItem('token')}` };
      const [categoryRes, productsRes] = await Promise.all([
        axios.get(`${API}/api/order-categories`, { headers }),
        axios.get(`${API}/api/order-products?category_id=${categoryId}`, { headers })
      ]);
      const categories = categoryRes.data || [];
      const foundCategory = categories.find(c => c.id === categoryId);
      setCategory(foundCategory);
      const availableProducts = (productsRes.data || []).filter(p => p.is_available !== false);
      setProducts(availableProducts);
    } catch (error) {
      toast.error('Fehler beim Laden der Daten');
    } finally {
      setLoading(false);
    }
  };

  const openProductModal = async () => {
    setShowProductModal(true);
    setProductTab('existing');
    setProductSearch('');
    try {
      const headers = { 'Authorization': `Bearer ${localStorage.getItem('token')}` };
      const res = await axios.get(`${API}/api/order-products`, { headers });
      setAllProducts(res.data || []);
    } catch { setAllProducts([]); }
  };

  // Vorhandenes Produkt zu dieser Kategorie hinzufügen
  const addExistingProduct = async (product) => {
    setAddingProduct(product.id);
    try {
      const headers = { 'Authorization': `Bearer ${localStorage.getItem('token')}` };

      if (product.source === 'backshop') {
        // Backshop-Produkt: als neuen order_product-Eintrag anlegen + zur Kategorie zuweisen
        await axios.post(`${API}/api/order-products`, {
          name: product.name,
          supplier_article_number: product.supplier_article_number,
          internal_article_number: product.internal_article_number,
          unit: product.unit,
          price_net: product.price_net,
          packaging_unit: product.packaging_unit,
          ean: product.ean,
          category_ids: [categoryId],
          is_available: true,
        }, { headers });
      } else {
        // Normales order_product: category_ids aktualisieren
        const updatedCategoryIds = [...new Set([...(product.category_ids || []), categoryId])];
        await axios.put(`${API}/api/order-products/${product.id}`, {
          ...product,
          category_ids: updatedCategoryIds,
        }, { headers });
      }

      toast.success(`"${product.name}" zu dieser Kategorie hinzugefügt`);
      await fetchData();
      // Alle Produkte neu laden
      const res = await axios.get(`${API}/api/order-products`, { headers });
      setAllProducts(res.data || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Fehler beim Hinzufügen');
    } finally { setAddingProduct(null); }
  };

  // Produkt aus dieser Kategorie entfernen (nicht aus dem System löschen)
  const removeProductFromCategory = async (product) => {
    setRemovingProduct(product.id);
    try {
      const headers = { 'Authorization': `Bearer ${localStorage.getItem('token')}` };
      const updatedCategoryIds = (product.category_ids || []).filter(id => id !== categoryId);

      if (updatedCategoryIds.length === 0) {
        // Letzte Kategorie → Produkt komplett löschen (war speziell für diese Kategorie)
        await axios.delete(`${API}/api/order-products/${product.id}`, { headers });
      } else {
        // Noch in anderen Kategorien → nur diese Kategorie aus der Liste entfernen
        await axios.put(`${API}/api/order-products/${product.id}`, {
          ...product,
          category_ids: updatedCategoryIds,
        }, { headers });
      }
      toast.success(`"${product.name}" aus Kategorie entfernt`);
      await fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Fehler beim Entfernen');
    } finally { setRemovingProduct(null); }
  };

  // Neues Produkt anlegen und direkt zu Kategorie zuweisen
  const saveNewProduct = async () => {
    if (!newProductForm.name.trim()) { toast.error('Produktname ist Pflicht'); return; }
    setSavingNewProduct(true);
    try {
      const headers = { 'Authorization': `Bearer ${localStorage.getItem('token')}` };
      await axios.post(`${API}/api/order-products`, {
        name: newProductForm.name.trim(),
        internal_article_number: newProductForm.internal_article_number.trim() || null,
        supplier_article_number: newProductForm.supplier_article_number.trim() || null,
        unit: newProductForm.unit || 'Stück',
        price_net: newProductForm.price_net ? parseFloat(newProductForm.price_net) : null,
        packaging_unit: newProductForm.packaging_unit.trim() || null,
        ean: newProductForm.ean.trim() || null,
        min_stock: newProductForm.min_stock ? parseInt(newProductForm.min_stock) : null,
        product_group: newProductForm.product_group.trim() || null,
        vat_rate: parseInt(newProductForm.vat_rate) || 19,
        category_ids: [categoryId],
        is_available: true,
      }, { headers });
      toast.success(`"${newProductForm.name}" angelegt und zur Kategorie hinzugefügt`);
      setNewProductForm({ name: '', internal_article_number: '', supplier_article_number: '', unit: 'Stück', price_net: '', packaging_unit: '', ean: '', min_stock: '', product_group: '', vat_rate: 19 });
      setShowProductModal(false);
      await fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Fehler beim Anlegen');
    } finally { setSavingNewProduct(false); }
  };

  // Produkte nach Warengruppe gruppieren
  const groupedProducts = React.useMemo(() => {
    const groups = {};
    const ungrouped = [];
    products.forEach(p => {
      if (p.product_group) {
        if (!groups[p.product_group]) groups[p.product_group] = [];
        groups[p.product_group].push(p);
      } else {
        ungrouped.push(p);
      }
    });
    // Ergebnis: sortierte Gruppen + ungrouped am Ende
    const result = Object.entries(groups).map(([name, items]) => ({ name, items }));
    if (ungrouped.length > 0) result.push({ name: null, items: ungrouped });
    return result;
  }, [products]);

  const hasGroups = products.some(p => p.product_group);

  // Viewport-Erkennung: Desktop-Tabelle und Mobile-Cards duerfen NIE gleichzeitig
  // gemountet sein — beide registrieren dieselbe Produkt-ID im SortableContext,
  // die unsichtbare Variante (0x0) ueberschreibt die sichtbare -> Drag&Drop tot.
  const [isMobileView, setIsMobileView] = useState(() => window.matchMedia('(max-width: 767px)').matches);
  useEffect(() => {
    const mq = window.matchMedia('(max-width: 767px)');
    const handler = (e) => setIsMobileView(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  // Automatische Farbe basierend auf Gruppenname (deterministisch)
  const groupColor = (name) => {
    if (!name) return { bg: 'bg-gray-100 dark:bg-gray-800', text: 'text-gray-700 dark:text-gray-300', border: 'border-gray-200' };
    const colors = [
      { bg: 'bg-blue-600', text: 'text-white', border: 'border-blue-600' },
      { bg: 'bg-emerald-600', text: 'text-white', border: 'border-emerald-600' },
      { bg: 'bg-violet-600', text: 'text-white', border: 'border-violet-600' },
      { bg: 'bg-amber-500', text: 'text-white', border: 'border-amber-500' },
      { bg: 'bg-rose-600', text: 'text-white', border: 'border-rose-600' },
      { bg: 'bg-cyan-600', text: 'text-white', border: 'border-cyan-600' },
      { bg: 'bg-orange-500', text: 'text-white', border: 'border-orange-500' },
      { bg: 'bg-indigo-600', text: 'text-white', border: 'border-indigo-600' },
    ];
    let hash = 0;
    for (let i = 0; i < name.length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash);
    return colors[Math.abs(hash) % colors.length];
  };

  // ── dnd-kit: Touch & Mouse Drag (funktioniert auf Desktop + Mobile) ─────────
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(TouchSensor, {
      activationConstraint: { delay: 150, tolerance: 8 },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragEnd = async ({ active, over }) => {
    if (!over || active.id === over.id) return;
    const oldIndex = products.findIndex(p => p.id === active.id);
    const newIndex  = products.findIndex(p => p.id === over.id);
    if (oldIndex === -1 || newIndex === -1) return;
    const updated = arrayMove(products, oldIndex, newIndex).map((p, i) => ({ ...p, sort_order: i }));
    setProducts(updated);
    const headers = { 'Authorization': `Bearer ${localStorage.getItem('token')}` };
    try {
      await axios.put(`${API}/api/order-products-sort`,
        updated.map(p => ({ id: p.id, sort_order: p.sort_order, source: p.source || 'order' })),
        { headers }
      );
    } catch { /* UI bleibt korrekt auch bei Fehler */ }
  };

  // Vorhandenes Produkt bearbeiten (Warengruppe, Art-Nr, etc.)
  const openEditExisting = (product) => {
    setEditingExistingProduct(product);
    setEditExistingForm({
      name: product.name || '',
      internal_article_number: product.internal_article_number || '',
      supplier_article_number: product.supplier_article_number || '',
      unit: product.unit || 'Stück',
      price_net: product.price_net || '',
      packaging_unit: product.packaging_unit || '',
      min_stock: product.min_stock || '',
      product_group: product.product_group || '',
      vat_rate: product.vat_rate ?? 19,
    });
  };

  const saveExistingEdit = async () => {
    if (!editingExistingProduct) return;
    setSavingExistingEdit(true);
    try {
      const headers = { 'Authorization': `Bearer ${localStorage.getItem('token')}` };
      await axios.put(`${API}/api/order-products/${editingExistingProduct.id}`, {
        ...editingExistingProduct,
        ...editExistingForm,
        price_net: editExistingForm.price_net ? parseFloat(editExistingForm.price_net) : null,
        min_stock: editExistingForm.min_stock ? parseInt(editExistingForm.min_stock) : null,
        product_group: editExistingForm.product_group?.trim() || null,
      }, { headers });
      toast.success('Produkt aktualisiert');
      setEditingExistingProduct(null);
      await fetchData();
      const res = await axios.get(`${API}/api/order-products`, { headers });
      setAllProducts(res.data || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Fehler beim Speichern');
    } finally { setSavingExistingEdit(false); }
  };

  const loadLastOrder = async () => {
    try {
      const response = await axios.get(`${API}/api/orders/category/${categoryId}/last`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      if (response.data && response.data.items) {
        const newCart = {};
        response.data.items.forEach(item => { newCart[item.product_id] = item.quantity; });
        setCart(newCart);
        toast.success('Letzte Bestellung übernommen!');
      } else {
        toast.info('Keine vorherige Bestellung gefunden');
      }
    } catch { toast.error('Fehler beim Laden der letzten Bestellung'); }
  };

  // Letzte Bestellung als Referenz laden (nur zur Anzeige, überschreibt cart NICHT)
  const loadLastOrderReference = async () => {
    try {
      const response = await axios.get(`${API}/api/orders/category/${categoryId}/last`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      if (response.data?.items) {
        const ref = {};
        response.data.items.forEach(item => { ref[item.product_id] = item.quantity; });
        setLastOrderQty(ref);
      }
    } catch { /* silent – Referenz ist optional */ }
  };

  const updateQuantity = (productId, delta) => {
    const p = products.find(pr => pr.id === productId);
    const step = Math.abs(delta) === 1 ? parseVpe(p) : Math.abs(delta);
    const dir  = delta > 0 ? 1 : -1;
    setCart(prev => {
      const newQty = Math.max(0, (prev[productId] || 0) + step * dir);
      if (newQty === 0) { const { [productId]: _, ...rest } = prev; return rest; }
      return { ...prev, [productId]: newQty };
    });
  };

  const setQuantity = (productId, value) => {
    const qty = parseInt(value) || 0;
    if (qty <= 0) { const { [productId]: _, ...rest } = cart; setCart(rest); }
    else setCart(prev => ({ ...prev, [productId]: qty }));
  };

  const setVpeQuantity = (productId, value) => {
    const qty = parseInt(value) || 0;
    if (qty <= 0) { const { [productId]: _, ...rest } = cartVpe; setCartVpe(rest); }
    else setCartVpe(prev => ({ ...prev, [productId]: qty }));
  };

  const getTotalItems = () => Object.values(cart).reduce((s, q) => s + q, 0);

  // VPE-Anzahl aus packaging_unit parsen ("12 x 1l" → 12, "12" → 12)
  const parseVpe = (p) => {
    if (!p?.packaging_unit) return 1;
    const m = String(p.packaging_unit).match(/^(\d+)/);
    return m ? parseInt(m[1]) : 1;
  };

  // Netto-Summe eines Produkts (Stk + VPE)
  const itemNetto = (productId) => {
    const p = products.find(pr => pr.id === productId);
    const stkTotal = (p?.price_net || 0) * (cart[productId] || 0);
    const vpeTotal = (p?.price_net || 0) * parseVpe(p) * (cartVpe[productId] || 0);
    return stkTotal + vpeTotal;
  };

  const getTotalAmount = () =>
    [...Object.keys(cart), ...Object.keys(cartVpe)]
      .filter((v, i, a) => a.indexOf(v) === i)
      .reduce((s, id) => s + itemNetto(id), 0);

  // MwSt-Aufschlüsselung für den Warenkorb-Footer
  const getTaxBreakdown = () => {
    const groups = {};
    [...Object.keys(cart), ...Object.keys(cartVpe)]
      .filter((v, i, a) => a.indexOf(v) === i)
      .forEach(id => {
        const p = products.find(pr => pr.id === id);
        const rate = p?.vat_rate ?? 19;
        const netto = itemNetto(id);
        groups[rate] = (groups[rate] || 0) + netto;
      });
    const netto  = Object.values(groups).reduce((s, n) => s + n, 0);
    const mwst   = Object.entries(groups).reduce((s, [r, n]) => s + n * Number(r) / 100, 0);
    return { groups, netto, mwst, brutto: netto + mwst };
  };

  const handleSubmitOrder = () => {
    if (getTotalItems() === 0) { toast.error('Bitte mindestens ein Produkt hinzufügen'); return; }
    setShowNotesDialog(true);
  };

  const confirmOrder = async () => {
    try {
      setSubmitting(true);
      const items = Object.entries(cart).map(([productId, quantity]) => {
        const product = products.find(p => p.id === productId);
        return {
          product_id: productId,
          product_name: product.name,
          supplier_article_number: product.supplier_article_number,
          internal_article_number: product.internal_article_number,
          unit: product.unit,
          price_net: product.price_net,
          packaging_unit: product.packaging_unit,
          quantity,
          quantity_vpe: cartVpe[productId] || null,
          vat_rate: product.vat_rate ?? 19,
        };
      });
      await axios.post(`${API}/api/orders`, {
        category_id: categoryId,
        category_name: category.name,
        items,
        notes: notes.trim() || null,
      }, { headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` } });

      const sentToSupplier = !!category?.supplier_email;
      toast.success(sentToSupplier
        ? `Bestellung abgeschickt! In der Bestellhistorie kannst du das PDF an ${category.supplier_email} senden.`
        : 'Bestellung erfolgreich abgeschickt!'
      );
      setCart({}); setCartVpe({}); setNotes(''); setShowNotesDialog(false);
      setTimeout(() => navigate('/bestellungen/historie'), 1500);
    } catch { toast.error('Fehler beim Absenden der Bestellung'); }
    finally { setSubmitting(false); }
  };

  // Produkte die NICHT in dieser Kategorie sind (für "Vorhandenes hinzufügen")
  const notInCategory = allProducts.filter(p =>
    !(p.category_ids || []).includes(categoryId) && p.is_available !== false
  );
  const filteredNotInCategory = notInCategory.filter(p =>
    !productSearch || p.name.toLowerCase().includes(productSearch.toLowerCase()) ||
    (p.internal_article_number || '').toLowerCase().includes(productSearch.toLowerCase()) ||
    (p.supplier_article_number || '').toLowerCase().includes(productSearch.toLowerCase())
  );

  if (loading) return (
    <DashboardLayout>
      <div className="flex items-center justify-center h-64">
        <div className="text-muted-foreground">Lädt...</div>
      </div>
    </DashboardLayout>
  );

  if (!category) return (
    <DashboardLayout>
      <div className="text-center py-12"><p>Kategorie nicht gefunden</p></div>
    </DashboardLayout>
  );

  const totalItems = getTotalItems();
  const totalAmount = getTotalAmount();

  return (
    <DashboardLayout>
      <div className="space-y-4 sm:space-y-6 pb-6">
        {/* Header */}
        <div>
          <Button variant="ghost" onClick={() => navigate(-1)} className="mb-2 -ml-2">
            <ArrowLeft className="h-4 w-4 mr-2" /> Zurück
          </Button>
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <span className="text-3xl sm:text-4xl">{category.icon}</span>
              <div>
                <h1 className="text-2xl sm:text-3xl font-bold" style={{fontFamily: 'Manrope, sans-serif'}}>
                  {category.name}
                </h1>
                <p className="text-sm sm:text-base text-muted-foreground mt-1">Produktkatalog und Bestellung</p>
              </div>
            </div>
            {/* Admin: Produkte verwalten */}
            {isAdmin && (
              <Button variant="outline" size="sm" onClick={openProductModal} className="shrink-0 gap-2 border-primary text-primary hover:bg-primary/5">
                <Settings className="h-4 w-4" />
                <span className="hidden sm:inline">Produkte verwalten</span>
              </Button>
            )}
          </div>
        </div>

        {/* Supplier Auto-Versand Banner */}
        {category?.supplier_email && (
          <div className="flex items-center gap-3 bg-blue-50 dark:bg-blue-950/20 border border-blue-200 dark:border-blue-800 rounded-xl px-4 py-3">
            <span className="text-blue-600 text-lg">📧</span>
            <div className="flex-1">
              <p className="text-sm font-semibold text-blue-800 dark:text-blue-300">Lieferantenversand bereit</p>
              <p className="text-xs text-blue-700 dark:text-blue-400">
                Nach dem Bestellen kannst du das PDF in der <strong>Bestellhistorie</strong> per Klick an <strong>{category.supplier_email}</strong> senden.
                {category.kundennummer && ` · Kundennr.: ${category.kundennummer}`}
              </p>
            </div>
          </div>
        )}

        {/* Actions Bar */}
        <div className="flex flex-col sm:flex-row gap-2">
          <Button variant="outline" onClick={loadLastOrder} className="w-full sm:w-auto">
            <History className="h-4 w-4 mr-2" /> Letzte Bestellung übernehmen
          </Button>
          <Button variant="outline" onClick={() => navigate('/bestellungen/historie')} className="w-full sm:w-auto">
            <History className="h-4 w-4 mr-2" /> Bestellhistorie
          </Button>
        </div>

        {/* Teilen mit Partner-Kunden */}
        {isAdmin && (
          <TagesgeschaeftShareBar
            variant="full"
            contentType="bestellkategorie"
            categoryId={category.id}
            subjectName={category.name}
            subjectIcon={category.icon}
          />
        )}

        {/* Products */}
        {products.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <Package className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-lg font-semibold mb-2">Noch keine Produkte</h3>
              <p className="text-sm text-muted-foreground mb-4">
                Für diese Kategorie wurden noch keine Produkte angelegt.
              </p>
              {isAdmin && (
                <Button onClick={openProductModal} className="gap-2">
                  <Plus className="h-4 w-4" /> Produkte hinzufügen
                </Button>
              )}
            </CardContent>
          </Card>
        ) : (
          <>
            {/* Produkte – nach Warengruppe gruppiert, mit dnd-kit Touch+Mouse Drag */}
            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
              <SortableContext items={products.map(p => p.id)} strategy={verticalListSortingStrategy}>
            <div className="space-y-4">
              {groupedProducts.map(({ name: groupName, items }) => {
                const color = groupColor(groupName);
                return (
                  <div key={groupName || '__ungrouped__'}>
                    {/* Gruppenheader (nur wenn Gruppen vorhanden) */}
                    {hasGroups && (
                      <div className={`flex items-center justify-between px-4 py-2.5 rounded-t-xl ${color.bg} ${color.text}`}>
                        <span className="font-semibold text-sm">
                          {groupName || 'Sonstige'}
                        </span>
                        <span className="text-xs opacity-80 font-medium">
                          {items.length} Produkt{items.length > 1 ? 'e' : ''}
                        </span>
                      </div>
                    )}

                    {/* Desktop-Tabelle (nur auf Desktop gemountet!) */}
                    {!isMobileView && (
                    <div className="hidden md:block">
                      <Card className={hasGroups ? 'rounded-t-none' : ''}>
                        <CardContent className="p-0">
                          <div className="overflow-x-auto">
                            <table className="w-full">
                              {!hasGroups && (
                                <thead className="bg-gray-50 dark:bg-gray-800 border-b">
                                  <tr>
                                    {isAdmin && <th className="px-2 py-3 w-10"></th>}
                                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 dark:text-gray-300">Produkt</th>
                                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 dark:text-gray-300">Art.-Nr. (intern)</th>
                                    <th className="px-4 py-3 text-right text-xs font-semibold text-gray-700 dark:text-gray-300">Preis</th>
                                    <th className="px-4 py-3 text-center text-xs font-semibold text-gray-700 dark:text-gray-300">Menge (Stk)</th>
                                    {category?.supplier_email && <th className="px-4 py-3 text-center text-xs font-semibold text-blue-700 dark:text-blue-400">Menge (VPE)</th>}
                                    <th className="px-4 py-3 text-right text-xs font-semibold text-gray-700 dark:text-gray-300">Summe</th>
                                    {isAdmin && <th className="px-4 py-3 w-10"></th>}
                                  </tr>
                                </thead>
                              )}
                              {hasGroups && (
                                <thead className="bg-gray-50 dark:bg-gray-800 border-b">
                                  <tr>
                                    {isAdmin && <th className="px-2 py-2 w-10"></th>}
                                    <th className="px-4 py-2 text-left text-xs text-gray-500">Produkt</th>
                                    <th className="px-4 py-2 text-left text-xs text-gray-500">Art.-Nr.</th>
                                    <th className="px-4 py-2 text-right text-xs text-gray-500">Preis</th>
                                    <th className="px-4 py-2 text-center text-xs text-gray-500">Menge (Stk)</th>
                                    {category?.supplier_email && <th className="px-4 py-2 text-center text-xs text-blue-600">Menge (VPE)</th>}
                                    <th className="px-4 py-2 text-right text-xs text-gray-500">Summe</th>
                                    {isAdmin && <th className="px-4 py-2 w-10"></th>}
                                  </tr>
                                </thead>
                              )}
                              <tbody>
                                {items.map((product) => {
                                  const qty = cart[product.id] || 0;
                                  const vpeQty = cartVpe[product.id] || 0;
                                  const vpeCount = parseVpe(product);
                                  const itemTotal = itemNetto(product.id);
                                  return (
                                    <SortableDesktopRow
                                      key={product.id}
                                      product={product}
                                      isAdmin={isAdmin}
                                      qty={qty}
                                      vpeQty={vpeQty}
                                      vpeCount={vpeCount}
                                      itemTotal={itemTotal}
                                      category={category}
                                      lastOrderQty={lastOrderQty}
                                      cartVpe={cartVpe}
                                      editingExistingProduct={editingExistingProduct}
                                      editExistingForm={editExistingForm}
                                      setEditExistingForm={setEditExistingForm}
                                      savingExistingEdit={savingExistingEdit}
                                      saveExistingEdit={saveExistingEdit}
                                      setEditingExistingProduct={setEditingExistingProduct}
                                      removingProduct={removingProduct}
                                      updateQuantity={updateQuantity}
                                      setQuantity={setQuantity}
                                      setVpeQuantity={setVpeQuantity}
                                      openEditExisting={openEditExisting}
                                      removeProductFromCategory={removeProductFromCategory}
                                    />
                                  );
                                })}
                              </tbody>
                            </table>
                          </div>
                        </CardContent>
                      </Card>
                    </div>
                    )}

                    {/* Mobile Cards – dnd-kit Touch Drag (nur auf Mobile gemountet!) */}
                    {isMobileView && (
                    <div className={`md:hidden space-y-2 ${hasGroups ? 'bg-white dark:bg-gray-900 border border-t-0 rounded-b-xl p-2' : 'space-y-3'}`}>
                      {items.map((product) => (
                        <SortableMobileCard
                          key={product.id}
                          product={product}
                          isAdmin={isAdmin}
                          cart={cart}
                          cartVpe={cartVpe}
                          lastOrderQty={lastOrderQty}
                          editingExistingProduct={editingExistingProduct}
                          editExistingForm={editExistingForm}
                          setEditExistingForm={setEditExistingForm}
                          savingExistingEdit={savingExistingEdit}
                          saveExistingEdit={saveExistingEdit}
                          setEditingExistingProduct={setEditingExistingProduct}
                          updateQuantity={updateQuantity}
                          setQuantity={setQuantity}
                          parseVpe={parseVpe}
                          openEditExisting={openEditExisting}
                          removeProductFromCategory={removeProductFromCategory}
                        />
                      ))}
                    </div>
                    )}
                  </div>
                );
              })}
            </div>
              </SortableContext>
            </DndContext>

            {/* Admin: Weiteres Produkt hinzufügen */}
            {isAdmin && (
              <button onClick={openProductModal}
                className="w-full flex items-center justify-center gap-2 py-3 rounded-xl border-2 border-dashed border-primary/30 text-primary/70 hover:border-primary hover:text-primary hover:bg-primary/5 transition-colors text-sm font-medium"
                data-testid="add-product-btn">
                <Plus className="h-4 w-4" /> Weiteres Produkt hinzufügen
              </button>
            )}
          </>
        )}

        {/* Summary & Submit */}
        {totalItems > 0 && (
          <Card className="border-2 border-blue-500 bg-blue-50 dark:bg-blue-950/20">
            <CardContent className="pt-4 pb-4">
              <div className="space-y-3">
                {/* Netto / MwSt / Brutto */}
                {(() => {
                  const { groups, netto, mwst, brutto } = getTaxBreakdown();
                  return (
                    <div className="space-y-1.5 text-sm">
                      {Object.entries(groups).sort().map(([rate, n]) => (
                        <div key={rate} className="flex justify-between text-gray-600 dark:text-gray-400">
                          <span>Netto ({rate}% MwSt)</span>
                          <span>{n.toFixed(2)} €</span>
                        </div>
                      ))}
                      <div className="flex justify-between text-gray-600 dark:text-gray-400 border-t pt-1">
                        <span>MwSt gesamt</span>
                        <span>{mwst.toFixed(2)} €</span>
                      </div>
                      <div className="flex justify-between font-bold text-lg text-blue-700 dark:text-blue-400 border-t border-blue-300 pt-1">
                        <span>Brutto</span>
                        <span>{brutto.toFixed(2)} €</span>
                      </div>
                      <div className="flex justify-between text-xs text-gray-500">
                        <span>Netto gesamt</span>
                        <span>{netto.toFixed(2)} €</span>
                      </div>
                    </div>
                  );
                })()}
                <Button onClick={handleSubmitOrder} className="w-full" size="lg" disabled={submitting}>
                  <ShoppingCart className="h-5 w-5 mr-2" />
                  Bestellung absenden ({totalItems} Artikel)
                </Button>
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Notes Dialog */}
      <Dialog open={showNotesDialog} onOpenChange={setShowNotesDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Bestellung absenden</DialogTitle>
            <DialogDescription>Optional: Notiz zur Bestellung</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="notes">Notizen (optional)</Label>
              <Input id="notes" placeholder="z.B. Bitte bis Freitag liefern..." value={notes} onChange={e => setNotes(e.target.value)} />
            </div>
            <div className="p-4 bg-gray-50 dark:bg-gray-800 rounded-lg space-y-2">
              <div className="flex justify-between text-sm">
                <span>Artikel gesamt:</span><span className="font-semibold">{totalItems}</span>
              </div>
              {(() => {
                const { groups, netto, mwst, brutto } = getTaxBreakdown();
                return (
                  <>
                    {Object.entries(groups).sort().map(([rate, n]) => (
                      <div key={rate} className="flex justify-between text-sm text-gray-600">
                        <span>Netto ({rate}%)</span><span>{n.toFixed(2)} €</span>
                      </div>
                    ))}
                    <div className="flex justify-between text-sm text-gray-600">
                      <span>MwSt</span><span>{mwst.toFixed(2)} €</span>
                    </div>
                    <div className="flex justify-between font-bold text-base border-t pt-1">
                      <span>Brutto</span><span className="text-blue-600">{brutto.toFixed(2)} €</span>
                    </div>
                  </>
                );
              })()}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowNotesDialog(false)} disabled={submitting}>Abbrechen</Button>
            <Button onClick={confirmOrder} disabled={submitting}>
              {submitting ? 'Wird abgeschickt...' : 'Jetzt bestellen'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Produkt-Management Modal (Admin only) ── */}
      <Dialog open={showProductModal} onOpenChange={setShowProductModal}>
        <DialogContent className="max-w-2xl max-h-[85vh] flex flex-col p-0 gap-0">
          <DialogHeader className="px-6 pt-5 pb-3 border-b shrink-0">
            <DialogTitle className="flex items-center gap-2">
              <Settings className="h-5 w-5 text-primary" />
              Produkte verwalten – {category?.name}
            </DialogTitle>
          </DialogHeader>

          {/* Tabs */}
          <div className="flex border-b shrink-0">
            {[['existing', 'Vorhandenes Produkt hinzufügen'], ['new', 'Neues Produkt anlegen']].map(([tab, label]) => (
              <button key={tab} onClick={() => setProductTab(tab)}
                className={`flex-1 py-3 text-sm font-medium transition-colors ${productTab === tab ? 'border-b-2 border-primary text-primary' : 'text-muted-foreground hover:text-foreground'}`}>
                {label}
              </button>
            ))}
          </div>

          <div className="flex-1 overflow-y-auto px-6 py-4">
            {/* Tab: Vorhandenes Produkt */}
            {productTab === 'existing' && (
              <div className="space-y-3">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input value={productSearch} onChange={e => setProductSearch(e.target.value)}
                    placeholder="Produkt oder Art.-Nr. suchen…" className="pl-9" autoFocus />
                </div>
                {filteredNotInCategory.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground text-sm">
                    {notInCategory.length === 0
                      ? 'Alle Produkte sind bereits in dieser Kategorie'
                      : 'Keine Produkte gefunden'}
                  </div>
                ) : (
                  <div className="space-y-2">
                    {filteredNotInCategory.map(product => (
                      <div key={product.id} className="rounded-lg border bg-muted/20 overflow-hidden">
                        <div className="flex items-center justify-between p-3 hover:bg-muted/40 transition-colors">
                          <div className="min-w-0 flex-1">
                            <p className="text-sm font-medium truncate">{product.name}</p>
                            <p className="text-xs text-muted-foreground font-mono">
                              {product.internal_article_number ? `Art.-Nr.: ${product.internal_article_number}` : ''}
                              {product.supplier_article_number ? ` · Lief.: ${product.supplier_article_number}` : ''}
                              {product.product_group ? ` · ${product.product_group}` : ''}
                              {product.unit ? ` · ${product.unit}` : ''}
                            </p>
                          </div>
                          <div className="flex gap-1.5 ml-3 shrink-0">
                            <Button size="sm" variant="ghost" className="h-8 w-8 p-0 text-muted-foreground hover:text-foreground"
                              onClick={() => editingExistingProduct?.id === product.id ? setEditingExistingProduct(null) : openEditExisting(product)}
                              title="Bearbeiten">
                              <Edit2 className="h-3.5 w-3.5" />
                            </Button>
                            <Button size="sm" variant="outline" className="gap-1.5"
                              onClick={() => addExistingProduct(product)}
                              disabled={addingProduct === product.id}
                              data-testid={`add-existing-${product.id}`}>
                              {addingProduct === product.id ? '...' : <><Plus className="h-3.5 w-3.5" />Hinzufügen</>}
                            </Button>
                          </div>
                        </div>
                        {/* Inline-Edit-Formular */}
                        {editingExistingProduct?.id === product.id && (
                          <div className="border-t px-3 pb-3 pt-2 bg-muted/10 space-y-2">
                            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">Produkt bearbeiten</p>
                            <div className="grid grid-cols-2 gap-2">
                              <div className="col-span-2 space-y-1">
                                <Label className="text-xs">Produktname</Label>
                                <Input value={editExistingForm.name || ''} onChange={e => setEditExistingForm(f => ({...f, name: e.target.value}))} className="h-8 text-sm" />
                              </div>
                              <div className="space-y-1">
                                <Label className="text-xs">Warengruppe</Label>
                                <Input value={editExistingForm.product_group || ''} onChange={e => setEditExistingForm(f => ({...f, product_group: e.target.value}))} placeholder="z.B. Frische, TK, Shop" className="h-8 text-sm" />
                              </div>
                              <div className="space-y-1">
                                <Label className="text-xs">Art.-Nr. intern (JET)</Label>
                                <Input value={editExistingForm.internal_article_number || ''} onChange={e => setEditExistingForm(f => ({...f, internal_article_number: e.target.value}))} className="h-8 text-sm font-mono" />
                              </div>
                              <div className="space-y-1">
                                <Label className="text-xs">Mindestbestand</Label>
                                <Input type="number" value={editExistingForm.min_stock || ''} onChange={e => setEditExistingForm(f => ({...f, min_stock: e.target.value}))} className="h-8 text-sm" />
                              </div>
                              <div className="space-y-1">
                                <Label className="text-xs">Einheit</Label>
                                <select value={editExistingForm.unit || 'Stück'} onChange={e => setEditExistingForm(f => ({...f, unit: e.target.value}))}
                                  className="h-8 w-full rounded-md border bg-background px-2 text-sm">
                                  {UNITS.map(u => <option key={u}>{u}</option>)}
                                </select>
                              </div>
                            </div>
                            <div className="flex gap-2 justify-end pt-1">
                              <Button variant="ghost" size="sm" onClick={() => setEditingExistingProduct(null)}>Abbrechen</Button>
                              <Button size="sm" onClick={saveExistingEdit} disabled={savingExistingEdit}>
                                {savingExistingEdit ? 'Speichert...' : 'Speichern'}
                              </Button>
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Tab: Neues Produkt anlegen */}
            {productTab === 'new' && (
              <div className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="sm:col-span-2 space-y-1">
                    <Label>Produktname <span className="text-destructive">*</span></Label>
                    <Input value={newProductForm.name} onChange={e => setNewProductForm(f => ({...f, name: e.target.value}))}
                      placeholder="z.B. Pute / Hähnchenbrust" autoFocus data-testid="new-product-name" />
                  </div>
                  <div className="space-y-1">
                    <Label>Interne Art.-Nr. (JET) <span className="text-muted-foreground text-xs">(empfohlen)</span></Label>
                    <Input value={newProductForm.internal_article_number}
                      onChange={e => setNewProductForm(f => ({...f, internal_article_number: e.target.value}))}
                      placeholder="z.B. 12345" className="font-mono" data-testid="new-product-art-nr" />
                  </div>
                  <div className="space-y-1">
                    <Label>Lieferanten Art.-Nr. <span className="text-muted-foreground text-xs">(optional)</span></Label>
                    <Input value={newProductForm.supplier_article_number}
                      onChange={e => setNewProductForm(f => ({...f, supplier_article_number: e.target.value}))}
                      placeholder="z.B. 99887" className="font-mono" />
                  </div>
                  <div className="space-y-1">
                    <Label>Einheit</Label>
                    <select value={newProductForm.unit} onChange={e => setNewProductForm(f => ({...f, unit: e.target.value}))}
                      className="h-9 w-full rounded-md border bg-background px-3 text-sm">
                      {UNITS.map(u => <option key={u}>{u}</option>)}
                    </select>
                  </div>
                  <div className="space-y-1">
                    <Label>Preis (netto, €) <span className="text-muted-foreground text-xs">(optional)</span></Label>
                    <Input type="number" min="0" step="0.01" value={newProductForm.price_net}
                      onChange={e => setNewProductForm(f => ({...f, price_net: e.target.value}))}
                      placeholder="z.B. 4.99" />
                  </div>
                  <div className="space-y-1">
                    <Label>MwSt-Satz</Label>
                    <select value={newProductForm.vat_rate ?? 19}
                      onChange={e => setNewProductForm(f => ({...f, vat_rate: parseInt(e.target.value)}))}
                      className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary">
                      <option value={19}>19% (Standard)</option>
                      <option value={7}>7% (Lebensmittel)</option>
                      <option value={0}>0% (Steuerfrei)</option>
                    </select>
                  </div>
                  <div className="space-y-1">
                    <Label>Verpackungseinheit <span className="text-muted-foreground text-xs">(optional)</span></Label>
                    <Input value={newProductForm.packaging_unit}
                      onChange={e => setNewProductForm(f => ({...f, packaging_unit: e.target.value}))}
                      placeholder="z.B. 1 KAR = 12 Stk" />
                  </div>
                  <div className="space-y-1">
                    <Label>Warengruppe <span className="text-muted-foreground text-xs">(optional – für Gruppierung)</span></Label>
                    <Input value={newProductForm.product_group}
                      onChange={e => setNewProductForm(f => ({...f, product_group: e.target.value}))}
                      placeholder="z.B. Frische, TK, Süßes, Shop" />
                    <p className="text-xs text-muted-foreground">Produkte gleicher Gruppe werden in der Bestellliste zusammengefasst</p>
                  </div>
                  <div className="space-y-1">
                    <Label>EAN-Barcode <span className="text-muted-foreground text-xs">(optional)</span></Label>
                    <Input value={newProductForm.ean} onChange={e => setNewProductForm(f => ({...f, ean: e.target.value}))}
                      placeholder="z.B. 4000349760661" className="font-mono" />
                  </div>
                  <div className="space-y-1">
                    <Label>Mindestbestand <span className="text-muted-foreground text-xs">(optional)</span></Label>
                    <Input type="number" min="0" value={newProductForm.min_stock}
                      onChange={e => setNewProductForm(f => ({...f, min_stock: e.target.value}))}
                      placeholder="z.B. 5" />
                    <p className="text-xs text-muted-foreground">Wird beim Bestellen als Hinweis angezeigt</p>
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="px-6 py-4 border-t shrink-0 flex justify-between">
            <Button variant="outline" onClick={() => setShowProductModal(false)}>Schließen</Button>
            {productTab === 'new' && (
              <Button onClick={saveNewProduct} disabled={savingNewProduct || !newProductForm.name.trim()}
                data-testid="save-new-product-btn">
                {savingNewProduct ? 'Speichert...' : 'Produkt anlegen & hinzufügen'}
              </Button>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
};

